/**
 * Mural de Recados — post-its que a secretária cola no balcão durante o dia.
 *
 * Coleção: `clinics/{clinicId}/muralNotes/{id}`.
 *
 * ─── Por que não existe cron de "virada do dia" ───────────────────────────
 * O post-it resolvido some do mural sozinho porque a tela só assina DUAS
 * consultas: (a) tudo que está em aberto, sem filtro de data — é isso que faz
 * o recado não-resolvido amanhecer colado lá no dia seguinte; e (b) os
 * resolvidos cujo `resolvedDayKey` é o dia de HOJE em Brasília. Quando o
 * relógio vira, a consulta (b) simplesmente para de casar e a coluna
 * Resolvido esvazia por conta própria. Nada é apagado, nada é agendado.
 *
 * ─── Por que as queries não têm orderBy ───────────────────────────────────
 * Um `where` + `orderBy` em campos diferentes exige índice composto, e sem o
 * índice o Firestore lança — o que aqui apareceria como "mural vazio" e
 * ninguém entenderia por quê. Como o volume é de dezenas de docs, ordenamos
 * no cliente (`sortNotes`) e ficamos com queries de igualdade pura, que
 * funcionam em qualquer projeto sem deploy de índice.
 *
 * ─── Acúmulo ──────────────────────────────────────────────────────────────
 * Não existe arquivamento automático por idade. Recado de clínica não
 * desaparece sozinho: some quando alguém resolve, e só. O que envelhece é o
 * selo (`noteAgeDays`) e a ordenação — o mais velho sobe pro topo da coluna.
 */
import { db } from './config';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  arrayUnion,
  type Unsubscribe,
} from 'firebase/firestore';
import type { MuralCategory, MuralChatSource, MuralComment, MuralNote, MuralStatus } from '@/types/models';

/** Dia corrente em Brasília no formato 'YYYY-MM-DD'. */
export function brasiliaDayKey(ms: number = Date.now()): string {
  return new Date(ms).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
}

/**
 * Idade do recado em dias corridos de Brasília (0 = hoje). Contamos por
 * DIA DE CALENDÁRIO, não por 24h: um recado das 18h de ontem tem 1 dia às
 * 9h de hoje, que é como a secretária conta.
 */
export function noteAgeDays(note: Pick<MuralNote, 'dayKey'>, today = brasiliaDayKey()): number {
  if (!note.dayKey) return 0;
  const ms = (ymd: string) => Date.parse(`${ymd}T12:00:00Z`);
  const diff = ms(today) - ms(note.dayKey);
  if (!Number.isFinite(diff) || diff <= 0) return 0;
  return Math.round(diff / 86_400_000);
}

function notesCol(clinicId: string) {
  return collection(db, 'clinics', clinicId, 'muralNotes');
}

/**
 * Ordem dentro da coluna. Abertos: mais VELHO primeiro (o que está encalhado
 * sobe, senão o recado antigo afunda e nunca é resolvido). Resolvidos: mais
 * RECENTE primeiro (é um extrato do que acabou de sair).
 */
export function sortNotes(notes: MuralNote[]): MuralNote[] {
  return [...notes].sort((a, b) =>
    a.status === 'resolvido'
      ? (b.resolvedAt ?? b.createdAt) - (a.resolvedAt ?? a.createdAt)
      : a.createdAt - b.createdAt,
  );
}

export interface NewMuralNote {
  text: string;
  category: MuralCategory;
  status: Extract<MuralStatus, 'anotado' | 'aguardando'>;
  patientId?: string;
  patientName?: string;
  patientNumber?: string;
  /** Preenchido quando o recado veio de "Colar no mural" numa mensagem do chat. */
  fromChat?: MuralChatSource;
  /** Pipoca pop-up na recepção até alguém dar "Ciente" (ver `pendingReceptionAlerts`). */
  receptionAlert?: boolean;
}

/**
 * Post-its que ainda devem pipocar na tela de UMA pessoa da recepção, na
 * ordem do mural (mais velho primeiro).
 *
 * - só os marcados com `receptionAlert` e sem "Ciente" de ninguém;
 * - nunca o que a própria pessoa colou;
 * - só os de HOJE — o que ficou sem ciente ontem está na coluna Anotado,
 *   que é a pauta; não precisa pipocar de manhã;
 * - respeita o escopo de agenda do perfil (Equipe → `allowedProfessionalIds`):
 *   recepção escopada só é avisada pelos médicos dela. Vazio = todos.
 */
export function pendingReceptionAlerts(
  notes: MuralNote[],
  opts: { uid?: string; scope?: string[]; today?: string },
): MuralNote[] {
  const today = opts.today ?? brasiliaDayKey();
  const scope = opts.scope ?? [];
  return notes.filter(
    (n) =>
      n.receptionAlert === true &&
      !n.receptionSeenAt &&
      n.status !== 'resolvido' &&
      n.createdBy !== opts.uid &&
      n.dayKey === today &&
      (scope.length === 0 || scope.includes(n.createdBy)),
  );
}

export interface MuralActor {
  uid: string;
  name?: string;
  /** Cargo por extenso — vai carimbado nos acréscimos. */
  roleLabel?: string;
}

export const MuralNoteService = {
  /**
   * Assina os recados EM ABERTO (anotado + aguardando), de qualquer dia.
   * Sem filtro de data — é o acúmulo.
   */
  subscribeOpen(clinicId: string, cb: (notes: MuralNote[]) => void, onError?: (e: Error) => void): Unsubscribe {
    const q = query(notesCol(clinicId), where('status', 'in', ['anotado', 'aguardando']));
    return onSnapshot(
      q,
      (snap) => cb(sortNotes(snap.docs.map((d) => d.data() as MuralNote))),
      (err) => {
        console.error('[mural] subscribeOpen:', err);
        onError?.(err);
      },
    );
  },

  /**
   * Assina os recados resolvidos HOJE. Ao virar o dia, o `dayKey` muda e a
   * coluna esvazia sozinha (ver cabeçalho do arquivo).
   */
  subscribeResolvedToday(
    clinicId: string,
    dayKey: string,
    cb: (notes: MuralNote[]) => void,
    onError?: (e: Error) => void,
  ): Unsubscribe {
    const q = query(notesCol(clinicId), where('resolvedDayKey', '==', dayKey));
    return onSnapshot(
      q,
      (snap) => cb(sortNotes(snap.docs.map((d) => d.data() as MuralNote))),
      (err) => {
        console.error('[mural] subscribeResolvedToday:', err);
        onError?.(err);
      },
    );
  },

  /** Histórico completo de um paciente — inclui resolvidos de dias passados. */
  async listByPatient(clinicId: string, patientId: string): Promise<MuralNote[]> {
    const snap = await getDocs(query(notesCol(clinicId), where('patientId', '==', patientId)));
    return sortNotes(snap.docs.map((d) => d.data() as MuralNote));
  },

  async create(clinicId: string, data: NewMuralNote, actor: MuralActor): Promise<MuralNote> {
    const ref = doc(notesCol(clinicId));
    const now = Date.now();
    const note: MuralNote = {
      id: ref.id,
      clinicId,
      status: data.status,
      category: data.category,
      text: data.text.trim(),
      createdAt: now,
      dayKey: brasiliaDayKey(now),
      createdBy: actor.uid,
      updatedAt: now,
    };
    if (actor.name) note.createdByName = actor.name;
    if (data.patientId) note.patientId = data.patientId;
    if (data.patientName) note.patientName = data.patientName;
    if (data.patientNumber) note.patientNumber = data.patientNumber;
    if (data.fromChat) {
      // Firestore rejeita `undefined` — só leva os campos presentes.
      const src: MuralChatSource = { channelId: data.fromChat.channelId, messageId: data.fromChat.messageId };
      if (data.fromChat.channelName) src.channelName = data.fromChat.channelName;
      if (data.fromChat.senderName) src.senderName = data.fromChat.senderName;
      if (data.fromChat.sentAt) src.sentAt = data.fromChat.sentAt;
      note.fromChat = src;
    }
    if (data.receptionAlert) note.receptionAlert = true;
    await setDoc(ref, note);
    return note;
  },

  /**
   * "Ciente" da recepção no pop-up. Não mexe em `updatedAt` — ler o aviso não
   * é movimentar o post-it.
   */
  async ackReceptionAlert(clinicId: string, noteId: string, actor: MuralActor): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'muralNotes', noteId);
    await updateDoc(ref, {
      receptionSeenAt: Date.now(),
      receptionSeenBy: actor.uid,
      receptionSeenByName: actor.name ?? null,
    });
  },

  /**
   * Move o post-it de coluna. Resolver carimba autor e `resolvedDayKey`;
   * voltar pra qualquer coluna aberta LIMPA esses campos — é o mesmo caminho
   * usado pelo Desfazer, então não existe estado meio-resolvido.
   */
  async move(clinicId: string, noteId: string, status: MuralStatus, actor: MuralActor): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'muralNotes', noteId);
    const now = Date.now();
    if (status === 'resolvido') {
      await updateDoc(ref, {
        status,
        updatedAt: now,
        resolvedAt: now,
        resolvedBy: actor.uid,
        resolvedByName: actor.name ?? null,
        resolvedDayKey: brasiliaDayKey(now),
      });
      return;
    }
    await updateDoc(ref, {
      status,
      updatedAt: now,
      resolvedAt: null,
      resolvedBy: null,
      resolvedByName: null,
      resolvedDayKey: null,
    });
  },

  /** Correção de texto/categoria do próprio post-it. */
  async edit(
    clinicId: string,
    noteId: string,
    patch: Partial<Pick<MuralNote, 'text' | 'category'>>,
  ): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'muralNotes', noteId);
    await updateDoc(ref, { ...patch, updatedAt: Date.now() });
  },

  /**
   * Acrescenta uma linha à conversa do post-it. `arrayUnion` é append atômico:
   * duas pessoas escrevendo ao mesmo tempo não se atropelam, e nada do que já
   * estava lá é reescrito. Não existe editar/apagar acréscimo — de propósito.
   */
  async addComment(clinicId: string, noteId: string, text: string, actor: MuralActor): Promise<void> {
    const ref = doc(db, 'clinics', clinicId, 'muralNotes', noteId);
    const now = Date.now();
    const comment: MuralComment = {
      id: `${now.toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
      text: text.trim(),
      at: now,
      by: actor.uid,
    };
    if (actor.name) comment.byName = actor.name;
    if (actor.roleLabel) comment.byRole = actor.roleLabel;
    await updateDoc(ref, { comments: arrayUnion(comment), updatedAt: now });
  },

  /** Post-it digitado errado. Não é o caminho de "resolver" — é rasgar. */
  async remove(clinicId: string, noteId: string): Promise<void> {
    await deleteDoc(doc(db, 'clinics', clinicId, 'muralNotes', noteId));
  },
};
